import * as React from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import {
  $editorNodes,
  $editorEdges,
  $setEditorNodes,
  $setEditorEdges,
} from '../../../_state/automationEditor.state';
import {
  $canUndo,
  $canRedo,
  $undo,
  $redo,
  $pushHistory,
  $initializeHistory,
  $resetHistory,
} from '../../../_state/flowHistory.state';

export type UseFlowHistoryOptions = {
  enabled?: boolean;
};

export const useFlowHistory = (options: UseFlowHistoryOptions = {}) => {
  const { enabled = true } = options;

  // Get state
  const nodes = useAtomValue($editorNodes);
  const edges = useAtomValue($editorEdges);
  const canUndo = useAtomValue($canUndo);
  const canRedo = useAtomValue($canRedo);

  // Get actions
  const setEditorNodes = useSetAtom($setEditorNodes);
  const setEditorEdges = useSetAtom($setEditorEdges);
  const undoAtom = useSetAtom($undo);
  const redoAtom = useSetAtom($redo);
  const pushHistory = useSetAtom($pushHistory);
  const initializeHistory = useSetAtom($initializeHistory);
  const resetHistory = useSetAtom($resetHistory);

  // Track previous state for change detection
  const lastPushedStateRef = React.useRef<string>('');

  // Initialize history on mount
  React.useEffect(() => {
    if (enabled && nodes.length > 0) {
      initializeHistory({ nodes, edges, timestamp: Date.now() });
      lastPushedStateRef.current = JSON.stringify({ nodes, edges });
    }
  }, []); // Only on mount

  // Push to history when state changes (debounced)
  React.useEffect(() => {
    if (!enabled) return;

    const currentState = JSON.stringify({ nodes, edges });
    if (currentState !== lastPushedStateRef.current) {
      // Use a small timeout to batch rapid changes
      const timeoutId = setTimeout(() => {
        pushHistory({ nodes, edges, timestamp: Date.now() });
        lastPushedStateRef.current = currentState;
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [nodes, edges, enabled, pushHistory]);

  // Undo function
  const undo = React.useCallback(() => {
    if (!enabled || !canUndo) return;

    const snapshot = undoAtom();
    if (snapshot) {
      setEditorNodes(snapshot.nodes, false); // false = don't mark as change
      setEditorEdges(() => snapshot.edges);
      lastPushedStateRef.current = JSON.stringify({
        nodes: snapshot.nodes,
        edges: snapshot.edges,
      });
    }
  }, [enabled, canUndo, undoAtom, setEditorNodes, setEditorEdges]);

  // Redo function
  const redo = React.useCallback(() => {
    if (!enabled || !canRedo) return;

    const snapshot = redoAtom();
    if (snapshot) {
      setEditorNodes(snapshot.nodes, false);
      setEditorEdges(() => snapshot.edges);
      lastPushedStateRef.current = JSON.stringify({
        nodes: snapshot.nodes,
        edges: snapshot.edges,
      });
    }
  }, [enabled, canRedo, redoAtom, setEditorNodes, setEditorEdges]);

  return {
    canUndo: enabled && canUndo,
    canRedo: enabled && canRedo,
    undo,
    redo,
    resetHistory,
  };
};
